import { IssueTypeEnum, RateGroup, SortDirectionEnum, Trip, TripKindEnum, TripTableSearch, TripTypeEnum } from '@/models/gen/graphql';
import { QueryInputType, Validation, queryInput, titleCase, zeroPadFlightNumber } from '@/utils';

import HasPermission from '@/components/HasPermission';
import { TripSortColumnEnum } from '@/features/Trips/components/TripsTable/hook';
import { TripsFiltersState } from '@/features/Trips/components/TripFilters';

export const getTripsAndIds = (tripRows: Trip[], tripState: Map<string, Trip>): [map: Map<string, Trip>, keys: string[]] => {
  if (!tripRows?.length) return [new Map(), []];
  const updatedRows = tripRows.map((row) => tripState.get(row.id) || row);
  // filter rows based on FE search
  const trips = updatedRows.reduce((acc: Map<string, Trip>, row: Trip): Map<string, Trip> => {
    acc.set(row.id, row);
    return acc;
  }, new Map<string, Trip>());
  // UNCOMMENT to reapply FE search logic:: const trips: Map<string, Trip> = searchTripsTableColumns(updatedRows, search);
  // format row ids for the table
  return [trips, Array.from(trips.keys())];
};

// takes the rows and search and returns the trips ids that match
export const searchTripsTableColumns = (rows: Trip[], search: string): Map<string, Trip> => {
  const output: Map<string, Trip> = new Map();
  const lowerSearchTerm = search.toLowerCase();
  for (let i = 0; i < rows.length; i++) {
    const node = rows[i];
    // if nothing to search then add all
    if (!search) {
      output.set(node.id, node);
      continue;
    }
    // if something to search then add only if search matches
    // limit search to searchable columns
    const searchableColumns = [
      node.type,
      node.scheduled,
      node.trackFlight?.actual ?? '',
      node.trackFlight?.label ?? '',
      node.trackFlight?.arrivalGate ?? '',
      node.trackFlight?.arrivalTerminal ?? '',
      node.kind,
      node.airportCode,
      node.servicerIataAirlineCode ?? '',
      zeroPadFlightNumber(node.flightNumber),
      node.loopName ?? '',
      node.pilots ?? 0,
      node.attendants ?? 0,
      node.driver?.employeeId ?? '',
      node.driver?.fullName ?? '',
      node.puLocation?.name ?? '',
      node.doLocation?.name ?? '',
      node.rate?.rate ?? '',
      node.vehicle?.trackingId ?? '',
      node.payerProvider?.displayName ?? '',
      node.importTime ?? '',
    ]
      .join(' ')
      .toLowerCase();

    const match = searchableColumns.includes(lowerSearchTerm);
    if (!match) continue;
    output.set(node.id, node);
  }
  return output;
};
export const getRateFormValueFromSelectedTrip = (trip: Trip): Partial<RateGroup> | string => {
  if (trip.rate?.rateGroupId) return trip.rate.rateGroupId;
  return formatRateGroupFromTrip(trip);
};
export const formatRateGroupFromTrip = (
  input: Trip
): Pick<
  Partial<RateGroup>,
  'id' | 'thisLocationId' | 'thatLocationId' | 'airportCode' | 'payerProviderId' | 'thisLocation' | 'thatLocation' | 'payerProvider'
> => ({
  id: input.rate?.rateGroupId,
  thatLocationId: input.puLocationId,
  thisLocationId: input.doLocationId,
  airportCode: input.airportCode,
  payerProviderId: input.payerProviderId,
  thatLocation: input.puLocation,
  thisLocation: input.doLocation,
  payerProvider: input.payerProvider,
});

// fields marked as N/A are not yet supported on the BE
const SEARCHABLE_COLUMNS = [
  'type', // enum check
  // node.trackFlight?.actual ?? '', N/A
  // node.trackFlight?.label ?? '', N/A
  // node.trackFlight?.arrivalGate ?? '', N/A
  // node.trackFlight?.arrivalTerminal ?? '', N/A
  'kind', // enum check
  'airportCode',
  'servicerIataAirlineCode',
  'flightNumber', // is flight number check | overriden by flightNumber field
  'loopName', // is loop name check | overriden by flightNumber field
  'pilots', // is number check
  'attendants', // is number check
  'driverEmployeeId',
  'driverFirstName',
  'driverLastName',
  'puLocationName',
  'doLocationName',
  'rateAmount', // permission check
  'vehicleTrackingId',
  'payerProviderDisplayName',
];

export const convertTripsFiltersStateToQuery = (
  filters: TripsFiltersState,
  sorting: Array<{ column: TripSortColumnEnum; direction: SortDirectionEnum }>
): TripTableSearch[] => {
  // default query
  const query: TripTableSearch = {
    latestScheduled: queryInput.date([filters.from, filters.to]),
  };

  // destructure whatever keys don't map to TripTableSearch
  const { format: _format, from: _from, to: _to, issueType, search, ...remainingFilters } = filters || {};
  // loop through rest of the filters
  for (const filterKey in remainingFilters) {
    if (!Validation.isTruthy(remainingFilters[filterKey])) continue;
    const filterValue = remainingFilters[filterKey];

    let key = filterKey as keyof TripTableSearch;
    if (filterKey === 'flightNumber') key = isLoopName(filterValue) ? 'loopName' : 'flightNumber';
    if (filterKey === 'rateAmount' && !HasPermission.check('allowSearchTripsByRate')) continue;

    // if there is a value, add it to the query
    const { value, type } = convertValueToQueryInput(filterKey, filterValue);

    query[key] = queryInput(value, type);
  }

  // apply issue type to search
  switch (issueType) {
    case IssueTypeEnum.Accident:
      query.accidentIssueCount = [queryInput([0], QueryInputType.NOTIN), queryInput([], QueryInputType.ISNOTNULL)];
      break;
    case IssueTypeEnum.Dispatch:
      query.dispatchIssueCount = [queryInput([0], QueryInputType.NOTIN), queryInput([], QueryInputType.ISNOTNULL)];
      break;
    case IssueTypeEnum.Incident:
      query.incidentIssueCount = [queryInput([0], QueryInputType.NOTIN), queryInput([], QueryInputType.ISNOTNULL)];
      break;
    case IssueTypeEnum.LostAndFound:
      query.lostAndFoundIssueCount = [queryInput([0], QueryInputType.NOTIN), queryInput([], QueryInputType.ISNOTNULL)];
      break;
  }

  // if we don't have a search, just return the query with the sorting
  if (!search?.trim()) return [applySortingToTripQuery(query, sorting)];

  // if we have a search, apply it to the query
  const output: TripTableSearch[] = [];
  const searchToInt = parseInt(search);
  const trimmed = search?.trim();
  let isSortingApplied = false; // to keep query clean we apply sorting only once
  SEARCHABLE_COLUMNS.forEach((column) => {
    // ensure query doesn't already have this column set by other filters
    if (query[column]) return;
    let isInt = false;
    // add validation
    switch (column) {
      case 'kind':
        if (!TripKindEnum[titleCase(search)]) return;
        break;
      case 'type':
        if (!TripTypeEnum[titleCase(search)]) return;
        break;
      case 'servicerIataAirlineCode':
      case 'airportCode':
        if (!new RegExp('^[a-zA-Z]{1,3}$').test(trimmed)) return;
        break;
      case 'flightNumber':
        if (!Number.isInteger(searchToInt)) return;
        if (searchToInt < 1 || searchToInt > 9999) return;
        isInt = true;
        break;
      case 'loopName':
        if (!isLoopName(search)) return;
        break;
      case 'pilots':
      case 'attendants':
        if (isNaN(searchToInt)) return;
        if (searchToInt < 0 || searchToInt > 99) return;
        isInt = true;
        break;
      case 'vehicleTrackingId':
        if (isNaN(searchToInt)) return; // not an explicit number field in BE
        break;
      case 'rateAmount':
        if (!HasPermission.check('allowSearchTripsByRate') || isNaN(parseFloat(search))) return;
        isInt = true;
        break;
      case 'driverFirstName':
      case 'driverLastName':
      case 'puLocationName':
      case 'doLocationName':
      case 'payerProviderDisplayName':
      case 'driverEmployeeId':
        if (trimmed.length < 1) return;
        break;
    }

    // clone query
    const queryWithColumn = {
      ...query,
    };
    // apply search
    switch (column) {
      // enums
      case 'kind':
        queryWithColumn[column] = queryInput(TripKindEnum[titleCase(search)], QueryInputType.OR);
        break;
      case 'type':
        queryWithColumn[column] = queryInput(TripTypeEnum[titleCase(search)], QueryInputType.OR);
        break;
      // floats
      case 'rateAmount': {
        const { value, type } = convertValueToQueryInput(column, `${parseFloat(search)}`);
        queryWithColumn[column] = queryInput(value, type);
        break;
      }
      default:
        // replace any non alphanumeric characters with % and add to query
        queryWithColumn[column] = queryInput(
          !isInt ? `%${trimmed.replace(/[^a-zA-Z0-9]/g, '%')}%` : parseInt(trimmed),
          QueryInputType.LIKE
        );
    }
    // apply sorting if not already applied
    if (!isSortingApplied) {
      // we only need to apply sorting to 1 query; The BE will append the sorting to whole final query output.
      output.push(applySortingToTripQuery(queryWithColumn, sorting));
      isSortingApplied = true;
      return;
    }
    output.push(queryWithColumn);
  });
  return output;
};

export const convertValueToQueryInput = (key: string, value: any): { type: QueryInputType; value: any } => {
  if (key === 'rateAmount' && (parseFloat(`${value}`) === 0 || value === 'NO_RATE')) return { type: QueryInputType.INORNULL, value: [0.0] };
  if (value === null) return { type: QueryInputType.ISNULL, value: [] };
  return { type: QueryInputType.OR, value };
};

export const applySortingToTripQuery = (
  query: TripTableSearch,
  sorting: Array<{ column: TripSortColumnEnum; direction: SortDirectionEnum }>
): TripTableSearch => {
  if (!sorting?.length) return query;
  const output = { ...query };

  for (let i = 0; i < sorting.length; i++) {
    const { column, direction } = sorting[i];
    if (!direction) continue;
    const { values = [], type = QueryInputType.DEFAULT } = output[column] || ({} as any); // TODO: fix this any
    output[column] = queryInput(values, type, direction, i);
  }

  return output;
};

export const isLoopName = (value: string): boolean => /\D/g.test(value) || value.length > 4;
